import { useMemo, useState } from "react";
import { TradeToEarnLeaderboardDto, TradeToEarnLeaderboardSchema } from "@cede/types";
import { API_ENDPOINT, getLeaderboardQueryKey, isServerApiError, type ApiHelper } from "@cede/utils";
import { useRealtimeLeaderboardStatus } from "./useRealtimeLeaderboard";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

export type LeaderBoard = {
  usersTable: { rank: number; user: string; points: number; highlight: boolean }[];
  userRank: number;
  totalUsersWithPoints: number;
  topPercentage: number | undefined;
  totalVolume: number;
};

export const MAX_USERS_IN_TABLE = 40;

const EMPTY_LEADERBOARD: TradeToEarnLeaderboardDto = {
  id: 0,
  campaign: "",
  epochId: -1,
  rewardVaultAddress: "",
  cooldownEnd: null,
  leaderboardRecords: [],
};

// @todo: remove these functions and use the apiHelper.getOrThrow method
export async function fetchLeaderboard(
  apiHelper: ApiHelper,
  campaignName: string,
  epochId: number,
  auth?: Parameters<ApiHelper["getWithAuth"]>[0] | null,
): Promise<TradeToEarnLeaderboardDto> {
  const queryParams = { campaign: campaignName, epoch: String(epochId) };

  let response;
  try {
    response = auth
      ? await apiHelper.getWithAuth(auth, API_ENDPOINT.LEADERBOARD, { queryParams })
      : await apiHelper.get(API_ENDPOINT.LEADERBOARD, { queryParams });
  } catch (error) {
    const statusCode = (error as { statusCode?: number }).statusCode;
    if (statusCode === 404 || statusCode === 403) {
      return EMPTY_LEADERBOARD;
    }
    throw error;
  }

  if (isServerApiError(response)) {
    if (response.statusCode === 404 || response.statusCode === 403) {
      return EMPTY_LEADERBOARD;
    }
    throw new Error(response.message);
  }

  const parsedResponse = TradeToEarnLeaderboardSchema.safeParse(response);
  if (!parsedResponse.success) {
    throw new Error(
      `Failed to parse leaderboard response for campaign ${campaignName} epoch ${epochId}: ${parsedResponse.error.message}`,
    );
  }

  return parsedResponse.data;
}

/**
 * Get the leaderboard for a campaign and epoch. The epoch needs to be provided to trigger the request
 *
 * @param campaignName
 * @param epoch - Epoch object with id property. Uses minimal type { id: number } to accommodate Zod 4 type inference differences for nested optional fields.
 * @returns
 */
export const useLeaderboard = (campaignName: string, epoch?: { id: number }) => {
  const { hubUser, authenticated, auth, isAuthStateStable } = useHubUser();
  const apiHelper = useApi();
  const [leaderboardUpdatedAt, setLeaderboardUpdatedAt] = useState<number | undefined>(undefined);
  const { active: isRealtimeActive } = useRealtimeLeaderboardStatus();

  const { isLoading, data } = useQuery({
    queryKey: [...getLeaderboardQueryKey(campaignName, epoch?.id), authenticated],
    queryFn: async () => {
      // Use authenticated request when user is logged in so whitelisted users
      // can access leaderboards of restricted campaigns.
      const leaderboard = await fetchLeaderboard(apiHelper, campaignName, epoch?.id ?? 0, authenticated ? auth : null);
      setLeaderboardUpdatedAt(Date.now());
      return leaderboard;
    },
    enabled: !!campaignName && !!epoch && isAuthStateStable,
    staleTime: 1_000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: isRealtimeActive ? false : 60_000,
  });

  const rewardedLeaderboardRecords = useMemo(() => {
    if (!data) return [];
    return data.leaderboardRecords.filter((record) => record.rewardTokenAmount.gt(0));
  }, [data]);

  const userLeaderboardRecord = useMemo(() => {
    if (!data) return undefined;
    return data.leaderboardRecords.find((record) => record.hubUser.defiAddress === hubUser?.defiAddress);
  }, [data, hubUser?.defiAddress]);

  const userTopPercentage = useMemo(() => {
    if (!userLeaderboardRecord) return 0;
    const totalUsersWithPoints = rewardedLeaderboardRecords.length;
    const positionPercentage =
      totalUsersWithPoints !== 0 ? Math.round((userLeaderboardRecord.rank / totalUsersWithPoints) * 100) : 0;
    // we round the percentage to the nearest 1, 5, 10, 20, 30, 50 or 0, if more than 50%
    const topPercentage =
      positionPercentage > 50 ? 0 : [1, 5, 10, 20, 30, 50].find((percentage) => positionPercentage <= percentage);

    return topPercentage || 0;
  }, [rewardedLeaderboardRecords, userLeaderboardRecord]);

  return {
    leaderboard: data,
    leaderboardUpdatedAt,
    isLoading,
    isAuthStateStable,
    userLeaderboardRecord,
    rewardedLeaderboardRecords,
    userTopPercentage,
  };
};
